import Image from "next/image";
import Link from "next/link";
import React from "react";
import { s3Static } from "utils/constants";
import { FaBolt, FaPlug, FaLightbulb, FaFan, FaTools, FaFire } from "react-icons/fa";
import { formatPrice } from "utils/handyman-price-data";
import { ELECTRICIAN_PRICE_PAGE, jobPagePath } from "data/handyman-job-pages";

// Electrician-specific service cards. Deliberately kept free of plumbing,
// painting and furniture copy so this page stays topically focused on
// "electrician singapore" rather than generic handyman terms.
//
// Prices are looked up from the live price list by exact itemName. They used to
// be typed into this file and could drift from the API. A card whose item is
// missing simply drops its price line.
const electricianServicesData = [
  {
    title: "Power Trips & Wiring Repair",
    desc: [
      "Tracing repeated power trips, dead circuits and damaged or overheating wiring behind sockets and switches",
      "Our electricians isolate the faulty circuit before replacing any part, so you only pay for what is actually broken",
    ],
    prices: [["Troubleshoot and repair faulty wiring", "Wiring repair"]],
    href: jobPagePath("power-trip-repair"),
    icon: FaTools,
  },
  {
    title: "Circuit Breaker & DB Box",
    desc: [
      "Replacing worn circuit breakers and installing new distribution boards (DB boxes)",
      "An upgrade is worth considering when an older board trips often or has no spare ways for new circuits",
    ],
    prices: [["Replace circuit breaker", "Circuit breaker"], ["Install electrical distribution board (DB box)", "New DB box"]],
    href: jobPagePath("db-box-replacement"),
    icon: FaBolt,
  },
  {
    title: "Lighting Installation",
    desc: [
      "Supply and installation of LED lights, downlights, dimmer switches and replacement light switches",
      "Suitable for HDB flats, BTO units, condominiums and offices across Singapore",
    ],
    prices: [["Install/replace LED light", "LED light"], ["Install / replace downlight", "Downlight"]],
    href: ELECTRICIAN_PRICE_PAGE + "#prices-electrical",
    linkLabel: "See lighting prices",
    icon: FaLightbulb,
  },
  {
    title: "Ceiling Fan Installation",
    desc: [
      "Mounting and wiring of new ceiling fans and wall fans, including replacement of existing fans",
      "We check the ceiling hook and wiring load before installation to make sure the mount is safe",
    ],
    prices: [["Install ceiling fan", "Ceiling fan"]],
    href: jobPagePath("ceiling-fan-installation"),
    icon: FaFan,
  },
  {
    title: "Power Points & Appliance Wiring",
    desc: [
      "New power outlets, plus dedicated 15A aircon points, 20A water heater points and 13A washing machine points",
      "Correctly rated dedicated circuits prevent overloading when you add high-draw appliances",
    ],
    prices: [["Supply and install 13AMP power point (non-conceal)", "New power outlet"], ["Supply and install 15AMP aircon point (non-conceal)", "Appliance point"]],
    href: ELECTRICIAN_PRICE_PAGE + "#prices-electrical",
    linkLabel: "See power point prices",
    icon: FaPlug,
  },
  {
    title: "Water Heater Repair & Installation",
    desc: [
      "Diagnosis, repair and replacement of instant and storage water heaters, including the electrical supply point",
      "Covers heaters that trip the breaker, produce no hot water or leak at the connection",
    ],
    prices: [["Replace instant water heater", "Instant heater"], ["Supply and install 20AMP water heater point (non-conceal)", "20A heater point"]],
    href: jobPagePath("water-heater-installation"),
    icon: FaFire,
  },
];

export const ElectricianServices = ({ handymanPriceList = [] }) => (
  <ServiceCardGrid
    cards={electricianServicesData}
    handymanPriceList={handymanPriceList}
    image="handyman/electrician-installing-lights.jpg"
    imageAlt="Licensed electrician installing lights in Singapore - StringsSG"
  />
);

// Shared card grid, also used by PlumberServices. Each card is
// { title, desc[], prices: [[itemName, label]], href, linkLabel?, icon }.
export const ServiceCardGrid = ({ cards, handymanPriceList = [], image, imageAlt }) => {
  const priceOf = (itemName) => handymanPriceList.find((item) => item.itemName === itemName)?.serviceCost

  return (
    <div className="max-w-5xl mx-auto px-4">
      {/* One shared image instead of six full-width ones - the per-card photos
          added ~2000px of mobile scroll without adding information. */}
      <div className="relative w-full aspect-21/9 rounded-2xl overflow-hidden mb-5">
        <Image
          src={s3Static + image}
          fill
          style={{ objectFit: "cover", objectPosition: "center" }}
          placeholder="blur"
          blurDataURL={s3Static + "placeholder.png"}
          alt={imageAlt}
          sizes="(max-width: 768px) 100vw, 1024px"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cards.map(({ icon: Icon, title, desc, prices, href, linkLabel }) => {
          const priceLine = prices
            .map(([itemName, label]) => {
              const cost = priceOf(itemName)
              return cost != null ? `${label} from ${formatPrice(cost)}` : null
            })
            .filter(Boolean)
            .join(", ")
          return (
            <div
              key={title}
              className="flex flex-col bg-white rounded-2xl border border-gray-200 p-4 hover:border-orange-200 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-start gap-3">
                <span className="shrink-0 flex items-center justify-center w-9 h-9 rounded-xl bg-orange-50 border border-orange-100">
                  <Icon className="text-orange-500 text-sm" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-gray-900 mb-1.5">{title}</h3>
                  <ul className="space-y-1 text-sm text-gray-600 leading-relaxed">
                    {desc.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                    {priceLine && <li className="font-semibold text-green-700">{priceLine}</li>}
                  </ul>
                </div>
              </div>
              <Link
                href={href}
                prefetch={false}
                className="mt-3 ml-12 inline-flex items-center min-h-10 text-sm font-bold text-orange-600 hover:underline"
              >
                {linkLabel || `${title} details`} &rarr;
              </Link>
            </div>
          )
        })}
      </div>
    </div>
  );
};
